class OurPeopleSlider {
  constructor() {
    this.slider = null;
    this.lerpEase = 0.1;
    this.scrollSpeed = 0.35;
    this.isDragging = false;
    this.hasBeenDragged = false;
    this.isInViewport = false;
    this.lastScrollY = window.scrollY;
    this.isInitialized = false;
    this.isScrolling = false;
    this.scrollStopTimeout = null;
    this.marqueeStates = [];
    this.originalTrackCounts = [];

    this.init();
  }

  init() {
    document.readyState === "loading"
      ? document.addEventListener("DOMContentLoaded", () => this.setup())
      : this.setup();
  }

  setup() {
    this.slider = document.querySelector(".wrg.our-people");
    if (!this.slider) return;

    const wrapper = this.slider.querySelector(".our-people__wrapper");
    if (!wrapper) return;

    const marquees = this.slider.querySelectorAll(".our-people__marquee");
    if (!marquees.length) return;

    this.sliderContainer = wrapper;

    this.sliderContainer.style.willChange = "transform";
    this.sliderContainer.style.backfaceVisibility = "hidden";
    this.sliderContainer.style.perspective = "1000px";
    this.sliderContainer.style.transform = "translateZ(0)";
    this.sliderContainer.style.isolation = "isolate";

    this.removeMarqueeAnimations();

    this.marquees = marquees;
    this.setupMarquees();

    gsap.registerPlugin(ScrollTrigger);

    this.calculateDimensions();
    this.setupEvents();
    this.setupDrag();
    this.setupResize();

    // Remeasure after layout/images settle — wrong set width causes seam jumps
    requestAnimationFrame(() => this.calculateDimensions());
    window.addEventListener(
      "load",
      () => this.calculateDimensions({ preservePositions: true }),
      { once: true },
    );

    gsap.ticker.add(this.update.bind(this));

    this.isInitialized = true;
  }

  removeMarqueeAnimations() {
    const marquees = this.slider.querySelectorAll(".our-people__marquee");
    marquees.forEach((marquee) => {
      marquee.style.animation = "none";
      marquee.style.animationDuration = "none";
      marquee.style.animationIterationCount = "none";
      marquee.style.animationName = "none";
      marquee.style.animationTimingFunction = "none";
    });
  }

  setupMarquees() {
    this.marquees.forEach((marquee, index) => {
      const tracks = marquee.querySelectorAll(".our-people__track");
      if (tracks.length > 0) {
        this.createInfiniteLoopForMarquee(marquee, tracks, index);
      }
    });
  }

  createInfiniteLoopForMarquee(marquee, originalTracks, marqueeIndex) {
    const tracksArray = Array.from(originalTracks);

    for (let i = 0; i < 5; i++) {
      tracksArray.forEach((track) => {
        const clone = track.cloneNode(true);
        marquee.appendChild(clone);
      });
    }

    this.originalTrackCounts[marqueeIndex] = tracksArray.length;
  }

  /**
   * One loop period = track width + flex gap between cloned tracks.
   * Measuring offsetLeft delta (not offsetWidth alone) avoids seam jumps.
   */
  getSetWidth(marquee) {
    const tracks = marquee.querySelectorAll(".our-people__track");
    if (!tracks.length) return 0;

    if (tracks.length >= 2) {
      const period = tracks[1].offsetLeft - tracks[0].offsetLeft;
      if (period > 0) return period;
    }

    const gap =
      parseFloat(
        getComputedStyle(marquee).columnGap || getComputedStyle(marquee).gap,
      ) || 0;
    return tracks[0].offsetWidth + gap;
  }

  /**
   * Keep position in the mid-clone window [-2set, -set) by jumping 1 set.
   * Adjust current by the same amount so lerp does not cross the seam.
   */
  wrapState(state, nextTarget) {
    const setWidth = state.singleSetWidth;
    if (!setWidth) {
      state.target = nextTarget;
      return;
    }

    let target = nextTarget;
    let current = state.current;

    while (target < -setWidth * 2) {
      target += setWidth;
      current += setWidth;
    }
    while (target >= -setWidth) {
      target -= setWidth;
      current -= setWidth;
    }

    state.target = target;
    state.current = current;
  }

  applyDelta(delta, { immediate = false } = {}) {
    this.marqueeStates.forEach((state) => {
      this.wrapState(state, state.target + delta);
      if (immediate) {
        state.current = state.target;
        gsap.set(state.el, {
          x: state.current,
          y: 0,
          overwrite: true,
        });
      }
    });
  }

  calculateDimensions({ preservePositions = false } = {}) {
    if (!this.marquees || !this.marquees.length) return;

    const previousStates = preservePositions
      ? this.marqueeStates.map((state) => ({
          current: state.current,
          target: state.target,
          singleSetWidth: state.singleSetWidth,
        }))
      : null;

    this.marqueeStates = [];

    this.marquees.forEach((marquee, index) => {
      const singleSetWidth = this.getSetWidth(marquee);
      if (!singleSetWidth) return;

      const startPos = -singleSetWidth * 2;
      let current = startPos;
      let target = startPos;

      if (previousStates && previousStates[index]) {
        const prev = previousStates[index];
        // Keep visual progress through the loop when set width changes on resize
        if (prev.singleSetWidth) {
          const progress =
            ((prev.target % prev.singleSetWidth) + prev.singleSetWidth) %
            prev.singleSetWidth;
          const scaled = (progress / prev.singleSetWidth) * singleSetWidth;
          target = -singleSetWidth - scaled;
          current = target;
        } else {
          current = prev.current;
          target = prev.target;
        }
      }

      const state = {
        el: marquee,
        singleSetWidth,
        current,
        target,
      };

      this.wrapState(state, state.target);
      state.current = state.target;

      this.marqueeStates.push(state);

      gsap.set(marquee, {
        x: state.current,
        y: 0,
        force3D: true,
      });
    });
  }

  setupEvents() {
    const section = this.slider.closest(".section--our-people");
    if (!section) return;

    ScrollTrigger.create({
      trigger: section,
      start: "top bottom",
      end: "bottom top",
      onToggle: (self) => (this.isInViewport = self.isActive),
    });

    this.scrollHandler = () => {
      if (window.innerWidth < 1200) return;

      const scrollDelta = window.scrollY - this.lastScrollY;
      this.lastScrollY = window.scrollY;

      if (!this.isDragging && Math.abs(scrollDelta) > 0) {
        this.isScrolling = true;

        clearTimeout(this.scrollStopTimeout);
        this.scrollStopTimeout = setTimeout(() => {
          this.isScrolling = false;
        }, 60);

        this.applyDelta(-scrollDelta * this.scrollSpeed);
      }
    };

    window.addEventListener("scroll", this.scrollHandler, { passive: true });
  }

  setupDrag() {
    let startX;
    let startY;
    let lastX;
    let isDraggingHorizontally = false;
    const slider = this.sliderContainer;

    const start = (e) => {
      startX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
      startY = e.type.includes("touch") ? e.touches[0].clientY : e.clientY;
      lastX = startX;
      isDraggingHorizontally = false;
      this.isDragging = false;

      if (!e.type.includes("touch")) {
        e.preventDefault();
      }
    };

    const move = (e) => {
      const currentX = e.type.includes("touch")
        ? e.touches[0].clientX
        : e.clientX;
      const currentY = e.type.includes("touch")
        ? e.touches[0].clientY
        : e.clientY;

      const deltaX = Math.abs(currentX - startX);
      const deltaY = Math.abs(currentY - startY);

      if (!isDraggingHorizontally && !this.isDragging) {
        if (deltaX > 5 || deltaY > 5) {
          if (deltaX > deltaY * 1.5) {
            isDraggingHorizontally = true;
            this.isDragging = true;
            this.hasBeenDragged = true;
            lastX = currentX;
          } else {
            return;
          }
        } else {
          return;
        }
      }

      if (!this.isDragging) return;

      if (e.type.includes("touch")) {
        e.preventDefault();
      }

      const dx = currentX - lastX;
      lastX = currentX;
      this.applyDelta(dx, { immediate: true });
    };

    const end = (e) => {
      this.isDragging = false;
      const isTouchDevice = e && e.type && e.type.includes("touch");
      if (isTouchDevice) {
        isDraggingHorizontally = false;
      }
    };

    slider.addEventListener("mousedown", start);
    slider.addEventListener("touchstart", start, { passive: true });
    slider.addEventListener("mousemove", move);
    slider.addEventListener("touchmove", move, { passive: false });
    slider.addEventListener("mouseup", end);
    slider.addEventListener("touchend", end);
  }

  setupResize() {
    let timeout;
    this.resizeHandler = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (!this.isInitialized) return;
        this.scrollSpeed = window.innerWidth < 768 ? 0.3 : 0.5;
        this.calculateDimensions({ preservePositions: true });
      }, 300);
    };
    window.addEventListener("resize", this.resizeHandler);
  }

  update() {
    if (!this.sliderContainer || !this.marqueeStates.length) return;

    if (this.isDragging) {
      return;
    }

    const lerp = this.isScrolling ? 0.18 : this.lerpEase;

    this.marqueeStates.forEach((state) => {
      state.current += (state.target - state.current) * lerp;
      gsap.set(state.el, {
        x: state.current,
        y: 0,
        overwrite: true,
      });
    });
  }
}

// Initialize
if (document.querySelector(".wrg.our-people")) {
  new OurPeopleSlider();
}
